import { EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import MentionNode from "../nodes/mention";
import { getContext } from "./context";
import { TypeAheadProvider, type TypeAheadResult, type TypeAheadState } from "./typeahead";

interface MentionUser {
	id: string;
	fullName: string;
	email: string;
	invitable?: boolean;
	user?: {
		id: string;
	};
	render?: TypeAheadResult["render"];
}

export default class MentionProvider extends TypeAheadProvider {
	extraItems: TypeAheadResult[];

	constructor(options: { extraUsers?: readonly MentionUser[] } = {}) {
		super(options);
		this.extraItems = [];

		if (this.options.extraUsers) {
			this.options.extraUsers.forEach((user: MentionUser) => {
				this.extraItems.push({
					id: user.id || user.user?.id || "",
					title: user.fullName,
					content: user.email,
					command: this.makeCommandForUser(user),
					render: user.render ?? undefined,
				});
			});
		}
	}

	get defaultOptions() {
		return {
			...super.defaultOptions,
			...{
				style: "inline",
				users: [],
			},
		};
	}

	get key() {
		return "@";
	}

	get replaceMatch() {
		return false;
	}

	makeCommandForUser = ({ id, fullName, invitable = false }: { id: string; fullName: string; invitable?: boolean }) => {
		return (state: EditorState, typeaheadState?: TypeAheadState) => {
			const selection = typeaheadState?.matchingSelection && typeaheadState.matchingSelection.resolve(state.doc);
			return MentionNode.insertMention({ id, name: fullName, invitable }, selection);
		};
	};

	search(view: EditorView, searchString: string): TypeAheadResult[] {
		let allItems = [];
		const context = getContext(view.state);
		if (context?.participants) {
			allItems = context.participants.map((user: MentionUser) => ({
				id: user.id || user.user?.id,
				title: user.fullName,
				content: user.email,
				command: this.makeCommandForUser({
					id: user.id || user.user?.id || "",
					fullName: user.fullName,
					invitable: user.invitable,
				}),
			}));
		}
		if (this.extraItems) {
			allItems = this.extraItems.concat(allItems);
		}
		if (searchString === "") {
			return allItems;
		}

		const items = [];
		for (const item of allItems) {
			const sources = [item.title];
			if (item.content) sources.push(item.content);

			const score = this.fuzzy(sources, searchString);
			if (score === undefined || score === -Infinity) continue;

			items.push({
				...item,
				order: () => -score,
			});
		}

		return items;
	}
}
